import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Newspaper } from 'lucide-react';
import { usePressReleases } from '@/lib/api/hooks/press-release';
import { Loader } from '@/components/ui/Loader';
import DashboardCard from '@/pages/dashboard/DashboardCard';

interface RecentArticleItem {
	id: string;
	title: string;
	image: string;
	date: string;
	author: string;
}

const RecentArticles = () => {
	const navigate = useNavigate();
	const { data: recentData, isLoading } = usePressReleases(
		{ page: 1, size: 3 },
		{ status: 'PUBLISH' }
	);

	const recentArticles = useMemo<RecentArticleItem[]>(() => {
		if (!recentData?.data?.data) return [];
		const articleList = Array.isArray(recentData.data.data) ? recentData.data.data : [];

		return articleList.map((article: { pressReleaseId: string; title: string; coverImage?: string; datePublished?: string; createdAt: string; username?: string }) => ({
			id: article.pressReleaseId,
			title: article.title,
			image: article.coverImage || 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image',
			date: new Date(article.datePublished || article.createdAt).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
			author: article.username || 'Admin',
		}));
	}, [recentData]);

	return (
		<DashboardCard
			title="Recent Articles"
			titleIcon={<Newspaper className="h-4 w-4 text-gray-500" />}
			headerAction={
				<Link to="/admin/press-release" className="text-sm font-semibold text-[#681628] hover:underline">
					View all
				</Link>
			}
		>
			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader size="md" variant="wine" />
				</div>
			) : recentArticles.length > 0 ? (
				<div className="space-y-3">
					{recentArticles.map((article: RecentArticleItem, index: number) => (
						<div
							key={article.id}
							className={`flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors ${index < recentArticles.length - 1 ? 'pb-3 border-b border-gray-100' : ''}`}
							onClick={() => navigate(`/admin/press-release/${article.id}`)}
						>
							<img
								src={article.image}
								alt={article.title}
								className="w-14 h-10 object-cover rounded-md flex-shrink-0"
							/>
							<div className="flex-1 min-w-0">
								<h3 className="font-semibold text-gray-800 text-xs line-clamp-2">{article.title}</h3>
								<p className="text-xs text-gray-500 mt-0.5">{article.date} | {article.author}</p>
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="text-center py-8">
					<p className="text-sm text-gray-500">No recent articles</p>
				</div>
			)}
		</DashboardCard>
	);
};

export default RecentArticles;